import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { socketService, SocketEventType, SocketEventData } from '../../services/socketService';
import { toast } from 'sonner';

interface SocketContextValue {
  isConnected: boolean;
  subscribe: (eventType: SocketEventType | 'all', callback: (data: SocketEventData) => void) => () => void;
  subscribeToCategory: (category: 'enquiry' | 'application' | 'resume' | 'career' | 'user', callback: (data: SocketEventData) => void) => () => void;
}

const SocketContext = createContext<SocketContextValue | null>(null);

export function SocketProvider({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Connect when provider mounts
    socketService.connect();

    // Check connection status periodically
    const interval = setInterval(() => {
      setIsConnected(socketService.getConnectionStatus());
    }, 1000);

    // Show connection status notifications
    const unsubscribe = socketService.subscribe('all', (event) => {
      // Show toast notifications for real-time updates
      const [category, action] = event.type.split(':');
      const categoryName = category.charAt(0).toUpperCase() + category.slice(1);
      
      switch (action) {
        case 'created':
          toast.info(`New ${categoryName} received`, { duration: 3000 });
          break;
        case 'updated':
          toast.info(`${categoryName} updated`, { duration: 2000 });
          break;
        case 'deleted':
          toast.info(`${categoryName} deleted`, { duration: 2000 });
          break;
        case 'restored':
          toast.info(`${categoryName} restored`, { duration: 2000 });
          break;
      }
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
      socketService.disconnect();
    };
  }, []);

  const subscribe = useCallback((eventType: SocketEventType | 'all', callback: (data: SocketEventData) => void) => {
    return socketService.subscribe(eventType, callback);
  }, []);

  const subscribeToCategory = useCallback((category: 'enquiry' | 'application' | 'resume' | 'career' | 'user', callback: (data: SocketEventData) => void) => {
    return socketService.subscribeToCategory(category, callback);
  }, []);

  return (
    <SocketContext.Provider value={{ isConnected, subscribe, subscribeToCategory }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

// Generic hook for subscribing to specific socket events
export function useSocketEvent(eventType: SocketEventType, callback: (data: any) => void) {
  const { subscribe } = useSocket();
  
  useEffect(() => {
    const unsubscribe = subscribe(eventType, (event) => {
      callback(event.data);
    });
    return unsubscribe;
  }, [subscribe, eventType, callback]);
}

// Custom hooks for specific data types
export function useEnquiryUpdates(onUpdate: (data: SocketEventData) => void) {
  const { subscribeToCategory } = useSocket();
  
  useEffect(() => {
    const unsubscribe = subscribeToCategory('enquiry', onUpdate);
    return unsubscribe;
  }, [subscribeToCategory, onUpdate]);
}

export function useApplicationUpdates(onUpdate: (data: SocketEventData) => void) {
  const { subscribeToCategory } = useSocket();
  
  useEffect(() => {
    const unsubscribe = subscribeToCategory('application', onUpdate);
    return unsubscribe;
  }, [subscribeToCategory, onUpdate]);
}

export function useResumeUpdates(onUpdate: (data: SocketEventData) => void) {
  const { subscribeToCategory } = useSocket();
  
  useEffect(() => {
    const unsubscribe = subscribeToCategory('resume', onUpdate);
    return unsubscribe;
  }, [subscribeToCategory, onUpdate]);
}

export function useCareerUpdates(onUpdate: (data: SocketEventData) => void) {
  const { subscribeToCategory } = useSocket();
  
  useEffect(() => {
    const unsubscribe = subscribeToCategory('career', onUpdate);
    return unsubscribe;
  }, [subscribeToCategory, onUpdate]);
}

export function useUserUpdates(onUpdate: (data: SocketEventData) => void) {
  const { subscribeToCategory } = useSocket();
  
  useEffect(() => {
    const unsubscribe = subscribeToCategory('user', onUpdate);
    return unsubscribe;
  }, [subscribeToCategory, onUpdate]);
}
